import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Minimize2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ChatMessages } from './chat/ChatMessages';
import { ChatInput } from './chat/ChatInput';
import { logger } from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export function LiveChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [chatId, setChatId] = useState(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const connectWs = useCallback(() => {
    if (!chatId || wsRef.current?.readyState === WebSocket.OPEN) return;
    const wsUrl = API.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsUrl}/api/support/chat/${chatId}/ws`);
    ws.onopen = () => { setConnected(true); clearTimeout(reconnectRef.current); };
    ws.onclose = () => {
      setConnected(false);
      reconnectRef.current = setTimeout(connectWs, 3000);
    };
    ws.onerror = (err) => { logger.error('WS error:', err); };
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'message') {
          setMessages(prev => [...prev, data.message]);
        }
      } catch (parseErr) { logger.error('WS message parse error:', parseErr); }
    };
    wsRef.current = ws;
  }, [chatId]);

  useEffect(() => {
    if (chatId) connectWs();
    return () => { wsRef.current?.close(); clearTimeout(reconnectRef.current); };
  }, [chatId, connectWs]);

  const startChat = async () => {
    try {
      const res = await fetch(`${API}/api/support/chat/start`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setChatId(data.chat_id);
      setMessages(data.messages || []);
    } catch (err) { logger.error('Failed to start chat:', err); }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!chatId) startChat();
  };

  const handleSend = () => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ message: input.trim() }));
    setInput('');
  };

  if (!user) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {open ? (
        <div className="w-[380px] h-[520px] bg-[#0A1222]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="live-chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-cyan-500/10 to-purple-500/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full pi-gradient flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">SendPI Support</p>
                <div className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'}`} />
                  <span className="text-white/40 text-xs">{connected ? 'Connected' : 'Connecting...'}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-lg hover:bg-white/5 flex items-center justify-center text-white/40 hover:text-white">
                <Minimize2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { setOpen(false); wsRef.current?.close(); setChatId(null); setMessages([]); }}
                className="w-7 h-7 rounded-lg hover:bg-white/5 flex items-center justify-center text-white/40 hover:text-white" data-testid="close-chat">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <ChatMessages messages={messages} userName={user?.name} messagesEndRef={messagesEndRef} />
          <ChatInput input={input} setInput={setInput} connected={connected} onSend={handleSend} />
        </div>
      ) : (
        <button
          data-testid="open-chat-btn"
          onClick={handleOpen}
          className="w-14 h-14 rounded-full pi-gradient shadow-lg shadow-purple-500/20 flex items-center justify-center text-white hover:scale-110 transition-transform"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
